import { lazy, Suspense, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { Breadcrumb, type Crumb } from "@/components/Breadcrumb";
import { QuoteIdBadge } from "@/components/QuoteIdBadge";
import { ReplaceFileButton } from "@/components/ReplaceFileButton";
import { ReplaceFileModal } from "@/components/ReplaceFileModal";
import { ReplaceFileContext } from "@/components/replaceFileContext";
import { ShareQuoteButton } from "@/components/ShareQuoteButton";
import { Pill } from "@/components/ui/Pill";
import type { AnalysisSnapshot, Quote } from "@/api/types";
import { maxUploadBytes, takesFileInput } from "@/processes/registry";
import type { FrontendModule } from "@/processes/types";

/**
 * Read-only production-flow card — LAZY on purpose (KAN-204).
 *
 * It renders React Flow (``@xyflow/react`` + ``@xyflow/system`` + d3,
 * ~446 kB of source), and this shell sits on the EAGER buyer path:
 * ``router.tsx`` → shells → ``lazyPages`` → ``QuoteDetail`` → here.
 * Imported statically it landed the whole graph library in the entry
 * chunk, which is what ``/embed`` — a configurator on a stranger's
 * WordPress page — had to download before its first pixel.
 *
 * Deferring costs nothing in practice: the card only renders when the
 * tenant turned ``show_production_flow`` on for that process (default
 * off), and it sits below the analysis panel, not at first paint.
 */
const OperationGraphPicker = lazy(() =>
  import("@/components/OperationGraphPicker").then((m) => ({
    default: m.OperationGraphPicker,
  })),
);

interface Props {
  /** Frontend module handle (registry-merged backend metadata + slot
   *  components). Drives ``AnalysisStats`` + ``ValidationPanel`` slots
   *  and the replace-file accepted extensions. */
  module: FrontendModule;
  /** Opaque quote slug — shown as the Q-XXXXX badge and used by the
   *  replace-file modal to look up cart carryover. */
  quoteId: string;
  /** Filename to render under the status pill (e.g. ``LN4.stl``). */
  fileName: string;
  /** Chain of process types to highlight in "Tok výroby". Typically
   *  ``[module.name, ...chainOpProcessTypes]``. The graph itself is
   *  read from the tenant operation graph admin and tints the
   *  selected nodes. */
  selectedProcessTypes: string[];
  /** Whether to render the "Tok výroby" production-flow card. Opt-in,
   *  driven by the operation's ``visualization_schema`` (a
   *  ``production_flow`` block → ``module.show_production_flow``). When
   *  false the card is omitted entirely. */
  showProductionFlow: boolean;
  /** Crumbs leading to the current page. The shell does NOT add the
   *  current page automatically — pass the full trail including the
   *  active node (no ``href``). */
  breadcrumb: Crumb[];
  /** Localised label for the replace-file button (e.g. "Nahradit DXF"
   *  / "Nahradit STL"). */
  replaceLabel: string;
  /** Localised modal title for the replace-file flow. */
  replaceModalTitle: string;
  /** Customer-facing analysis snapshot used by ``module.AnalysisStats``
   *  and ``module.ValidationPanel``. */
  analysis: AnalysisSnapshot;
  /** Active language for navigation prefix. */
  lang: string;
  /** Preview / viewer node — DXF SVG, three.js mesh, or whatever a
   *  future module needs. Pages render this directly because
   *  the prop surface is too module-specific to fit a registry slot
   *  (overlays, mode tabs, orientation toggles, …). */
  preview?: ReactNode;
  /** Optional slot above the page header (e.g. batch nav). */
  topBanner?: ReactNode;
  /** Optional slot under the analysis stats (e.g. RepairCompare on
   *  the primary when the BE flagged a repair). */
  belowAnalysis?: ReactNode;
  /** Called by ``ReplaceFileModal`` after a successful replace when
   *  the page can't rely on a ``/quote/:id`` route swap. When omitted,
   *  the modal navigates to ``/quote/:id``. */
  onReplaced?: (newQuote: Quote) => void;
  /** Sticky right-column content — the per-process configurator,
   *  including its own price card and add-to-cart button. Below ``lg``
   *  the shell renders it directly under the page header, above the
   *  preview (KAN-935). */
  children: ReactNode;
}

/**
 * Shared page-shell for any product configurator (every registered
 * process). Each process declares its
 * preview, analysis stats and validation panel through the registry;
 * the shell composes them into the same two-column layout customers
 * already know from the primary flow. The "Tok výroby" graph and the
 * tenant-configurable discounts (``QuantityTierChips``, rendered by
 * the per-process configurator) are wired in identically across
 * processes — no hard-coded chains, no per-process branching here.
 */
export function ConfiguratorPageShell({
  module,
  quoteId,
  fileName,
  selectedProcessTypes,
  showProductionFlow,
  breadcrumb,
  replaceLabel,
  replaceModalTitle,
  analysis,
  lang,
  preview,
  topBanner,
  belowAnalysis,
  onReplaced,
  children,
}: Props): JSX.Element {
  const { t: tQuote } = useTranslation("quote");
  const [isReplaceOpen, setIsReplaceOpen] = useState(false);

  const AnalysisStats = module.AnalysisStats;
  const ValidationPanel = module.ValidationPanel;

  // Bere tahle operace od zákazníka soubor? Rozhoduje o tom metadata
  // operace, ne větvení na slug; ``fallbackProcess`` v ``QuoteDetail``
  // deklaruje soubor, takže výpadek ``/processes`` nepřepne stránku do
  // nesouborového režimu u položky, která soubor potřebuje.
  //
  // Řídí dvě věci:
  //
  //   * „Nahradit soubor" jen tam, kde nějaký soubor je. Operace bez
  //     souboru (``requires_file: false``, KAN-736) nabízela do KAN-773
  //     výměnu blobu, který nikdy neexistoval — a modal by za ním poslal
  //     upload do operace, která žádné přijímané typy nedeklaruje.
  //   * Stavový štítek. „✓ Analýza dokončena" popisovalo krok, který se
  //     u nesouborové služby nekoná: nic se nenahrává a nic se
  //     neanalyzuje (KAN-802, návaznost na vlastní stavové texty
  //     nesouborové větve z KAN-770).
  const hasFileInput = takesFileInput(module);

  // The replace-file upload re-analyzes with the module's own
  // ``process_type`` — validated on the BE.
  const processName = String(module.name);

  return (
    <main className="mx-auto max-w-6xl px-6 pb-16 pt-6">
      {/* Tři buňky, ne dvě. Hlavička stránky (breadcrumb + stavový řádek)
          je vlastní buňka, aby se pod ``lg`` dal konfigurátor s cenou
          vsunout MEZI ni a náhled dílu: na mobilu jinak zákazník nevidí
          ani jednu volbu ani cenu, dokud neodscrolluje celý náhled
          (KAN-935). Na ``lg`` a výš je pravý sloupec umístěný explicitně
          přes celou výšku mřížky, takže rozvržení zůstává to původní
          dvousloupcové. */}
      <div className="grid items-start gap-x-6 gap-y-4 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
        <section className="space-y-4">
          <Breadcrumb crumbs={breadcrumb} />

          {topBanner}

          <header className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <Pill tone="ok" icon={<span aria-hidden="true">✓</span>}>
                  {tQuote(hasFileInput ? "analysis.completed" : "fileless.ready")}
                </Pill>
                <QuoteIdBadge quoteId={quoteId} />
                {fileName && (
                  <span
                    className="mono truncate text-sm text-ink-2"
                    data-testid="page-file-name"
                  >
                    {fileName}
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {hasFileInput && (
                <ReplaceFileButton
                  label={replaceLabel}
                  onClick={() => setIsReplaceOpen(true)}
                />
              )}
              <ShareQuoteButton />
            </div>
          </header>
        </section>

        <section className="lg:col-start-2 lg:row-span-2 lg:row-start-1">
          <ReplaceFileContext.Provider
            value={hasFileInput ? () => setIsReplaceOpen(true) : null}
          >
            {children}
          </ReplaceFileContext.Provider>
        </section>

        <section className="space-y-4">
          {preview}

          {ValidationPanel && <ValidationPanel analysis={analysis} />}

          {belowAnalysis}

          {AnalysisStats && <AnalysisStats analysis={analysis} />}

          {showProductionFlow && (
            <Suspense fallback={null}>
              <OperationGraphPicker
                selectedProcessTypes={selectedProcessTypes}
              />
            </Suspense>
          )}
        </section>
      </div>

      {isReplaceOpen && hasFileInput && (
        <ReplaceFileModal
          lang={lang}
          oldQuoteSlug={quoteId}
          processName={processName}
          acceptedFileTypes={module.accepted_file_types}
          maxBytes={maxUploadBytes(module)}
          title={replaceModalTitle}
          onReplaced={onReplaced}
          onClose={() => setIsReplaceOpen(false)}
        />
      )}
    </main>
  );
}
